import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/storage';
import { take } from 'rxjs/operators';

import { AuthenticationService } from '../authentication.service';
import { MediaStorageService } from '../media-storage.service';
import { UUIDService } from '../uuid.service';

@Injectable()
export class FirestoreMediaStorageService extends MediaStorageService {
    private tenantId: string;

    constructor(
        private authenticationService: AuthenticationService,
        private fireStorage: AngularFireStorage,
        private httpClient: HttpClient,
        private uuidService: UUIDService
    ) {
        super();

        this.tenantId = this.authenticationService.user.tenantId;
    }

    async delete(url: string): Promise<void> {
        await this.fireStorage.storage.refFromURL(url).delete();
    }

    async getMediaType(url: string): Promise<'image' | 'video'> {
        const metadata = await this.fireStorage.storage.refFromURL(url).getMetadata();
        const contentType = metadata && metadata.contentType;

        if (contentType.includes('image')) {
            return 'image';
        }

        if (contentType.includes('video')) {
            return 'video';
        }

        return 'image';
    }

    async replace(file: File, url: string): Promise<string> {
        const upload = await this.fireStorage.storage.refFromURL(url).put(file);
        const downloadUrl = await upload.ref.getDownloadURL();
        return downloadUrl;
    }

    async upload(file: File, endpoint: string): Promise<string> {
        const filename = this.uuidService.generate();
        const filePath = this.buildFileUploadLocation(endpoint, filename);

        const upload = await this.fireStorage.upload(filePath, file);
        const downloadUrl = await upload.ref.getDownloadURL();

        return downloadUrl;
    }

    async download(fileUrl: string, filename: string): Promise<void> {
        const downloadURL = await this.fireStorage
            .refFromURL(fileUrl)
            .getDownloadURL()
            .toPromise();
        
        this.httpClient
            .get(downloadURL, { responseType: 'blob' })
            .pipe(take(1))
            .subscribe((result) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(result);
                a.download = filename;
                a.click();
            });
    }

    private buildFileUploadLocation(endpoint: string, filename: string): string {
        return `${this.tenantId}/${endpoint}/${filename}`;
    }
}
